/**
 * Cart store — manages marked items and cart state.
 * Persisted in localStorage so items survive page reload.
 */
import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { MarkedItem, CartItem } from '../features/delivery/types'

interface CartState {
  /** All items (marked + in-cart) */
  items: CartItem[]

  /** Add a newly marked item */
  addMarkedItem: (item: MarkedItem) => void

  /** Remove an item by id */
  removeItem: (id: string) => void

  /** Move a marked item into the cart */
  addToCart: (id: string) => void

  /** Remove from cart (back to marked) */
  removeFromCart: (id: string) => void

  /** Update quantity */
  updateQuantity: (id: string, quantity: number) => void

  /** Update item name */
  updateItemName: (id: string, name: string) => void

  /** Update item price */
  updateItemPrice: (id: string, price: number | null) => void

  /** Clear all items for a shop */
  clearShopItems: (shopId: string) => void

  /** Clear everything */
  clearAll: () => void

  /** Get items for a specific shop */
  getShopItems: (shopId: string) => CartItem[]

  /** Get only in-cart items grouped by shop */
  getCartByShop: () => Record<string, CartItem[]>

  /** Total item count (in cart) */
  cartItemCount: () => number

  /** All marked items (not in cart) */
  getMarkedItems: () => CartItem[]

  /** Get items whose 3D point projects onto a given frame */
  getItemsForFrame: (frameIndex: number) => CartItem[]
}

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],

      addMarkedItem: (item: MarkedItem) => {
        const cartItem: CartItem = { ...item, quantity: 1, inCart: true }
        set((state) => ({ items: [...state.items, cartItem] }))
      },

      removeItem: (id: string) => {
        set((state) => ({ items: state.items.filter((i) => i.id !== id) }))
      },

      addToCart: (id: string) => {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, inCart: true } : i)),
        }))
      },

      removeFromCart: (id: string) => {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, inCart: false } : i)),
        }))
      },

      updateQuantity: (id: string, quantity: number) => {
        if (quantity < 1) return
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, quantity } : i)),
        }))
      },

      updateItemName: (id: string, name: string) => {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, name } : i)),
        }))
      },

      updateItemPrice: (id: string, price: number | null) => {
        set((state) => ({
          items: state.items.map((i) => (i.id === id ? { ...i, price } : i)),
        }))
      },

      clearShopItems: (shopId: string) => {
        set((state) => ({ items: state.items.filter((i) => i.shopId !== shopId) }))
      },

      clearAll: () => set({ items: [] }),

      getShopItems: (shopId: string) => {
        return get().items.filter((i) => i.shopId === shopId)
      },

      getCartByShop: () => {
        const cartItems = get().items.filter((i) => i.inCart)
        const grouped: Record<string, CartItem[]> = {}
        for (const item of cartItems) {
          if (!grouped[item.shopId]) grouped[item.shopId] = []
          grouped[item.shopId].push(item)
        }
        return grouped
      },

      cartItemCount: () => {
        return get().items.filter((i) => i.inCart).length
      },

      getMarkedItems: () => {
        return get().items.filter((i) => !i.inCart)
      },

      getItemsForFrame: (frameIndex: number) => {
        return get().items.filter((i) => {
          // Item is on this exact frame
          if (i.frameIndex === frameIndex) return true
          // Item has a projection onto this frame
          if (i.projections[frameIndex]) return true
          return false
        })
      },
    }),
    {
      name: 'dukkan-cart',
      // Only persist serializable data (projections as Record is fine)
      partialize: (state) => ({ items: state.items }),
    }
  )
)

// Expose store on window for E2E tests
if (typeof window !== 'undefined') {
  ;(window as any).__cartStore = useCartStore
}
